/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2019 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import React from 'react';
import {useDispatch} from 'react-redux';
import {Toast} from '@react/react-spectrum/Toast';
import './Notification.scss';
import {REMOVE_NOTIFICATION} from '../../types';
import classNames from 'classnames';

interface NotificationProps {
	key: string;
	name: string;
	text: string;
	className?: string;
	isOpen?: boolean;
	variant?: string;
	actionLabel?: string;
	closeOnAction?: boolean;
	closable?: string;
	onActionCallback?: Function;
	onCloseCallback?: Function;
}

export const Notification: React.FC<NotificationProps> = ({
	key,
	name,
	text,
	isOpen = true,
	className = '',
	variant = 'info',
	actionLabel = '',
	closable = true,
	closeOnAction = true,
	onActionCallback = () => null,
	onCloseCallback = () => null,
}) => {
	const dispatch = useDispatch();

	const handleOnAction = () => {
		onActionCallback();
		if (closeOnAction) {
			dispatch({type: REMOVE_NOTIFICATION, payload: {name}});
		}
	};

	const handleOnClose = () => {
		onCloseCallback();
		dispatch({type: REMOVE_NOTIFICATION, payload: {name}});
	};

	if (isOpen) {
		return (
			<Toast
				closable={closable}
				key={key}
				className={classNames('generic-notification-toast', className)}
				variant={variant}
				actionLabel={actionLabel}
				onClose={() => handleOnClose()}
				onAction={() => handleOnAction()}
			>
				{text}
			</Toast>
		);
	}
	return null;
};

/**
 * Create the dispatch payload for a new notification
 * Example: createNotificationPayload('Unsupported PSW', 'PSW does not support this browser', {message: 'Learn more', className: 'learn-more', callback: () => console.log('calling customizableError')});
 * @param {string} name - Name of the notification
 * @param {string} message - Message of the notification
 * @param {Object} [options={}] - optional props to customize Notification
 */
export function createNotificationPayload(name: string, text: string, options: genericObject = {}) {
	return {
		name,
		new: {
			name,
			text,
			...options,
		},
	};
}

/**
 * Create the dispatch payload for removing a notification
 * Example: removeNotificationPayload('Unsupported PSW');
 * @param {string} name - Name of the notification to remove
 */
export function removeNotificationPayload(name: string) {
	return {
		name,
	};
}
